/**
 * components/Toast/ToastItem.tsx
 * ──────────────────────────────────────────────────────────────────────────
 * The toast card itself, matching the design exactly:
 *   bottom-right · min 300 / max 420 wide · 11×13 padding · ink background ·
 *   6px radius · 12.5px / 1.45 white text · 3px left border tinted by tone ·
 *   `0 6px 24px -8px rgba(0,0,0,0.45)` shadow
 *
 * The design's toast carries ONE line. This renders an optional second line
 * beneath it at 72% opacity, which is where the real backend message goes —
 * utils/errorHandler.ts passes the action as the title and the API's own
 * sentence as the body, so the user sees what the server actually said rather
 * than a message this app invented.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Platform, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { neutral, series, status } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size, leading } from '../../theme/typography';
import type { ToastTone } from './types';

/** The 3px left border, per tone. `plain` is white — see the design source. */
const ACCENT: Record<ToastTone, string> = {
  success: series.open,          // #4CB782
  danger: status.danger.dot,     // #EB5757
  warn: series.purpose,          // #DEB949
  plain: neutral.surface,        // #FFFFFF
};

export interface ToastItemProps {
  tone: ToastTone;
  title?: string;
  message?: string;
}

export function ToastItem({ tone, title, message }: ToastItemProps) {
  return (
    <View style={[styles.card, { borderLeftColor: ACCENT[tone] }]}>
      {!!title && (
        <Text style={styles.title} numberOfLines={2}>
          {title}
        </Text>
      )}
      {!!message && (
        // Up to five lines: a validation or conflict message from the API can
        // run long, and truncating it mid-sentence defeats the point of
        // surfacing the backend's own words.
        <Text style={[styles.message, !!title && styles.messageSpaced]} numberOfLines={5}>
          {message}
        </Text>
      )}
    </View>
  );
}

const shadow: ViewStyle = Platform.select({
  web: { boxShadow: '0 6px 24px -8px rgba(0,0,0,0.45)' } as unknown as ViewStyle,
  ios: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.28,
    shadowRadius: 12,
  },
  default: { elevation: 8 },
}) as ViewStyle;

const styles = StyleSheet.create({
  card: {
    // `pointerEvents: auto` on the card while the host layer stays
    // transparent — the toast never blocks the UI behind it.
    alignSelf: 'flex-end',
    minWidth: 300,
    maxWidth: 420,
    paddingVertical: 11,
    paddingHorizontal: 13,
    backgroundColor: neutral.ink,
    borderRadius: radii.box,
    borderLeftWidth: 3,
    ...shadow,
  },
  title: {
    ...sans('500'),
    fontSize: size.button,
    lineHeight: leading(size.button),
    color: neutral.surface,
  },
  message: {
    ...sans('400'),
    fontSize: size.button,
    lineHeight: leading(size.button),
    color: 'rgba(255,255,255,0.72)',
  },
  messageSpaced: { marginTop: 2 },
});

export default ToastItem;
